/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import type { ResourceType, RulesetType } from '@modules/network-security-lib/types'
import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import type { TrafficFilterInfo } from '@modules/network-user-api/v1/types'

import IpFilterForm from './IpFilterForm'
import PrivateConnectionForm from './PrivateConnectionForm'
import ResourceTypeField from './fields/ResourceTypeField'
import HostedTrafficFilterHandler from './HostedTrafficFilterHandler'
import ServerlessTrafficFilterCreateHandler from './ServerlessTrafficFilterHandler/CreateHandler'
import ServerlessTrafficFilterUpdateHandler from './ServerlessTrafficFilterHandler/UpdateHandler'

import type { TrafficFilterHandlerRefProps } from './types'

type Props = {
  onClose: () => void
  rulesetType: RulesetType
  editing?:
    | {
        type: 'hosted'
        ruleset: TrafficFilterRulesetInfo
      }
    | {
        type: 'serverless'
        ruleset: TrafficFilterInfo
      }
}

/**
 * Main routing component for traffic filter forms.
 */
const TrafficFilterForm: React.FunctionComponent<Props> = ({ onClose, rulesetType, editing }) => {
  const isNewRuleset = !editing

  const handlerRef = useRef<TrafficFilterHandlerRefProps | null>(null)
  const save = () => handlerRef.current?.save()

  const [resourceType, setResourceType] = useState<ResourceType | null>(editing?.type || null)

  const [isSubmittable, setIsSubmittable] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<unknown>()

  const onRulesetChange = useCallback((args: { isSubmittable: boolean }) => {
    setIsSubmittable(args.isSubmittable)
  }, [])

  const onFormStateChange = useCallback((args: { isSaving: boolean; error: unknown }) => {
    setIsSaving(args.isSaving)
    setError(args.error)
  }, [])

  const form = useMemo(() => {
    if (resourceType === 'hosted') {
      return (
        <HostedTrafficFilterHandler
          ref={handlerRef}
          onClose={onClose}
          rulesetType={rulesetType}
          ruleset={editing?.ruleset as TrafficFilterRulesetInfo | undefined}
          onRulesetChange={onRulesetChange}
          onFormStateChange={onFormStateChange}
        >
          {(props) =>
            rulesetType === 'ipFilter' ? (
              <IpFilterForm {...props} />
            ) : (
              <PrivateConnectionForm {...props} />
            )
          }
        </HostedTrafficFilterHandler>
      )
    }

    if (resourceType === 'serverless') {
      if (isNewRuleset) {
        return (
          <ServerlessTrafficFilterCreateHandler
            ref={handlerRef}
            onClose={onClose}
            rulesetType={rulesetType}
            ruleset={undefined}
            onRulesetChange={onRulesetChange}
            onFormStateChange={onFormStateChange}
          >
            {(props) => <IpFilterForm {...props} />}
          </ServerlessTrafficFilterCreateHandler>
        )
      }

      return (
        <ServerlessTrafficFilterUpdateHandler
          ref={handlerRef}
          onClose={onClose}
          rulesetType={rulesetType}
          ruleset={editing.ruleset as TrafficFilterInfo}
          onRulesetChange={onRulesetChange}
          onFormStateChange={onFormStateChange}
        >
          {(props) => <IpFilterForm {...props} />}
        </ServerlessTrafficFilterUpdateHandler>
      )
    }

    return null // sanity
  }, [rulesetType, resourceType])

  const title = useMemo(() => {
    if (rulesetType === 'ipFilter') {
      return isNewRuleset ? (
        <FormattedMessage
          id='trafficFilterForm.create.title.ipFilter'
          defaultMessage='Create IP filter'
        />
      ) : (
        <FormattedMessage
          id='trafficFilterForm.edit.title.ipFilter'
          defaultMessage='Edit IP filter'
        />
      )
    }

    return isNewRuleset ? (
      <FormattedMessage
        id='trafficFilterForm.create.title.privateConnection'
        defaultMessage='Create private connection'
      />
    ) : (
      <FormattedMessage
        id='trafficFilterForm.edit.title.privateConnection'
        defaultMessage='Edit private connection'
      />
    )
  }, [rulesetType, isNewRuleset])

  return (
    <EuiFlyout onClose={onClose} size='40rem' aria-labelledby='trafficFilterFormTitle'>
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id='trafficFilterFormTitle'>{title}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiForm>
          <ResourceTypeField
            value={resourceType}
            onChange={setResourceType}
            isServerlessComingSoon={rulesetType === 'privateConnection'}
            isDisabled={!isNewRuleset}
          />
          <EuiSpacer size='m' />
          {form}
          <EuiSpacer size='m' />
          {error && <CuiAlert type='danger'>{error}</CuiAlert>}
        </EuiForm>
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onClose} data-test-id='cancel-button'>
              <FormattedMessage id='ipFilterForm.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              fill={true}
              isLoading={isSaving}
              isDisabled={!resourceType || !isSubmittable}
              onClick={save}
            >
              {isNewRuleset ? (
                <FormattedMessage id='ipFilterForm.create' defaultMessage='Create' />
              ) : (
                <FormattedMessage id='ipFilterForm.save' defaultMessage='Save' />
              )}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default TrafficFilterForm
